/* eslint-disable react/forbid-component-props */
import clsx from 'clsx'
import React, {
  Children,
  createContext,
  useContext,
  useEffect,
  useReducer,
} from 'react'
import type {
  Dispatch,
  EventHandler,
  FocusEvent,
  ReactElement,
  FocusEventHandler,
  KeyboardEvent,
  MouseEvent,
  MouseEventHandler,
  PropsWithChildren,
  ReactNode,
} from 'react'
import Checkbox from '../Checkbox/Checkbox'
import { useId } from '../hooks/useId'
import ExpandLess from '../icons/ExpandLess'
import ExpandMore from '../icons/ExpandMore'
import { Keys } from '../utilities/keyboard'
import type { NestedListActions } from './Actions'
import { NestedListActionTypes } from './Actions'
import List from './List'
import { ListNestedLevel, useList } from './ListContext'

export type ItemState = {
  itemState: ListItemStateType
  nestedListId: string | undefined
  open: boolean
}

export const ItemContext = createContext<UseItemValues | null>(null)

export type ListItemEndAdornmentType = 'element' | 'icon' | 'icon-button'

export type ListItemStartAdornmentType = 'avatar' | 'checkbox' | 'icon'

export type ListItemStateType = 'default' | 'disabled' | 'selected' | 'inverse'

type ItemButtonStyles = {
  [key in ListItemStateType]: string
}

type ItemIndents = {
  [key in ListNestedLevel]: string
}

type ItemInsets = {
  [key in ListItemStartAdornmentType]: string
}

interface EndAdornmentProps {
  disabled?: boolean
  nested?: boolean
  type?: ListItemEndAdornmentType
}

export interface ItemProps {
  children?: ReactElement
  defaultOpen?: boolean
  disabled?: boolean
  endAdornment?: ReactNode
  endAdornmentType?: ListItemEndAdornmentType
  inset?: ListItemStartAdornmentType
  inverse?: boolean
  onBlur?: FocusEventHandler<HTMLElement>
  onClick?: EventHandler<KeyboardEvent | MouseEvent>
  onFocus?: FocusEventHandler<HTMLElement>
  onMouseEnter?: MouseEventHandler<HTMLElement>
  onMouseLeave?: MouseEventHandler<HTMLElement>
  primary?: ReactNode
  secondary?: ReactNode
  selected?: boolean
  startAdornment?: ReactNode
  startAdornmentType?: ListItemStartAdornmentType
}

interface StartAdornmentProps {
  type?: ListItemStartAdornmentType
  disabled?: boolean
}

interface TextProps {
  id: string
  primary: ReactNode
  secondary?: ReactNode
  disabled?: boolean
}

const buttonStyles: ItemButtonStyles = {
  default: 'focus-visible:ring-inset focus:outline-none cursor-pointer',
  disabled: 'bg-transparent cursor-not-allowed',
  inverse: 'focus-visible:ring-inset focus:outline-none cursor-pointer',
  selected:
    'focus-visible:ring-inset-selected focus:outline-none cursor-pointer border-l-4 border-black-90',
}

// TODO add better default label support for expand/collapse buttons
const indents: ItemIndents = {
  [ListNestedLevel.None]: '',
  [ListNestedLevel.One]: 'ml-4', // 1rem
  [ListNestedLevel.Two]: 'ml-12', // 3rem
  [ListNestedLevel.Three]: 'ml-20', // 5rem
  [ListNestedLevel.Four]: 'ml-28', // 7rem
}

const insets: ItemInsets = {
  avatar: 'pl-14',
  checkbox: 'pl-11',
  icon: 'pl-11',
}

const textStyle = {
  default: { primary: 'text-black-90', secondary: 'text-black-60' },
  disabled: { primary: 'text-black-30', secondary: 'text-black-30' },
  inverse: { primary: 'text-white-90', secondary: 'text-white-60' },
  selected: { primary: 'text-black-90', secondary: 'text-black-60' },
}

const runReducer = (state: ItemState, action: NestedListActions): ItemState =>
  reducers[action.type](state, action)

export type ItemReducers = {
  [ActionType in NestedListActionTypes]: (
    state: ItemState,
    action: NestedListActions,
  ) => ItemState
}
export const reducers: ItemReducers = {
  [NestedListActionTypes.ToggleOpen]: (state) => {
    return { ...state, open: !state.open }
  },
  [NestedListActionTypes.RegisterListId]: (
    state,
    action: Extract<
      NestedListActions,
      { type: NestedListActionTypes.RegisterListId }
    >,
  ) => {
    return { ...state, nestedListId: action.id }
  },
  [NestedListActionTypes.UnregisterListId]: (state) => {
    return { ...state, nestedListId: undefined }
  },
  [NestedListActionTypes.UpdateItemState]: (
    state,
    action: Extract<
      NestedListActions,
      { type: NestedListActionTypes.UpdateItemState }
    >,
  ) => {
    return { ...state, itemState: action.itemState }
  },
}

type UseItemValues = [ItemState, Dispatch<NestedListActions> | null]
// Used by List to send dispatch about List's ID value back to Item.
export const useItem = (): UseItemValues =>
  useContext(ItemContext) as UseItemValues

function Item({
  children = undefined,
  defaultOpen = false,
  disabled = false,
  endAdornment,
  endAdornmentType = 'icon',
  inset,
  inverse = false,
  onBlur,
  onClick,
  onFocus,
  onMouseEnter,
  onMouseLeave,
  primary,
  secondary,
  selected = false,
  startAdornment,
  startAdornmentType,
}: ItemProps): JSX.Element {
  const id = `coconut-list-item-text-${useId()}`
  const { dense, nested } = useList()
  const [state, dispatch] = useReducer(runReducer, {
    itemState: disabled
      ? 'disabled'
      : selected
      ? 'selected'
      : inverse
      ? 'inverse'
      : 'default',
    open: defaultOpen,
  } as ItemState)

  const components: Record<string, ReactElement | null> = {
    nestedList: null,
  }

  const handleBlur = (event: FocusEvent<HTMLElement>) => {
    if (onBlur && !disabled) {
      onBlur(event)
    }
  }

  const handleClick = (event: KeyboardEvent | MouseEvent) => {
    if (components.nestedList && !disabled) {
      dispatch({ type: NestedListActionTypes.ToggleOpen })
    }

    if (onClick && !disabled) {
      onClick(event)
    }
  }

  const handleFocus = (event: FocusEvent<HTMLElement>) => {
    if (onFocus && !disabled) {
      onFocus(event)
    }
  }

  const handleMouseEnter = (event: MouseEvent<HTMLElement>) => {
    if (onMouseEnter && !disabled) {
      onMouseEnter(event)
    }
  }

  const handleMouseLeave = (event: MouseEvent<HTMLElement>) => {
    if (onMouseLeave && !disabled) {
      onMouseLeave(event)
    }
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    // avoid multiple activations when key is held down
    if (event.repeat) return

    if (
      onClick &&
      event.target === event.currentTarget &&
      (event.key === Keys.Space || event.key === Keys.Enter) &&
      !disabled
    ) {
      // prevent default behaviour of space like scrolling the page
      event.preventDefault()
      handleClick(event)
    }
  }

  useEffect(() => {
    dispatch({
      type: NestedListActionTypes.UpdateItemState,
      itemState: disabled
        ? 'disabled'
        : selected
        ? 'selected'
        : inverse
        ? 'inverse'
        : 'default',
    })
  }, [disabled, selected, inverse])

  const ignoreDense = endAdornmentType === 'icon-button' && !secondary

  const vertPadding =
    endAdornmentType === 'icon-button' && !secondary
      ? 'py-3'
      : dense && !ignoreDense
      ? 'py-1'
      : 'py-2'

  const buttonProps = {
    onBlur: handleBlur,
    onClick: handleClick,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    tabIndex: disabled ? -1 : 0,
    disabled: disabled ? disabled : undefined,
    // aria-pressed should not be used alongside aria-expanded.
    // https://github.com/WordPress/gutenberg/issues/21716
    'aria-pressed': selected ? true : false,
  }

  children &&
    Children.forEach(children, (child) => {
      switch (child.type) {
        case List:
          components.nestedList = child
          break
        default:
          break
      }
    })

  const toggleButtonProps = {
    onBlur: handleBlur,
    onClick: handleClick,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    tabIndex: disabled ? -1 : 0,
    disabled: disabled ? disabled : undefined,
    ...(components.nestedList
      ? {
          'aria-expanded': state.open,
          'aria-controls': state.nestedListId,
        }
      : null),
  }

  // If has a nested list item, is automatically a button
  const button = !!(onClick || components.nestedList)
  const ChildComponent = button ? 'button' : 'div'
  const listItemClasses = {
    className: inset ? insets[inset] : '',
  }

  const interactiveEndAdornment =
    endAdornmentType && endAdornmentType !== 'icon'
  const isCheckbox = startAdornmentType === 'checkbox'

  const getCheckbox = (): JSX.Element | undefined => {
    if (!startAdornment || !isCheckbox) {
      return
    }

    return (
      // TODO fix typescript error
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      <Checkbox.Input {...startAdornment.props}>
        <Text
          disabled={disabled}
          id={id}
          primary={primary}
          secondary={secondary}
        />
      </Checkbox.Input>
    )
  }

  const getLeftPadding = () => {
    if (button && state.itemState === 'selected') {
      return nested > ListNestedLevel.None ? 'pl-7' : 'pl-3'
    }

    return nested > ListNestedLevel.None ? 'pl-8' : 'pl-4'
  }

  return (
    <ItemContext.Provider value={[state, dispatch]}>
      {/* Avoid rendering empty class="" if no values exist */}
      <li {...(listItemClasses.className && listItemClasses)}>
        {components.nestedList ? (
          <>
            <span
              className={clsx(
                'flex items-center',
                !disabled && button && 'hover:bg-black-5 active:bg-black-10',
                indents[nested],
                selected ? 'bg-primary-10' : '',
              )}
            >
              {/* cannot have selected state AND be expand/collapse item */}
              <button
                className={clsx(
                  'flex w-full truncate items-center',
                  vertPadding,
                  textStyle[state.itemState].primary,
                  button ? buttonStyles[state.itemState] : '',
                  getLeftPadding(),
                )}
                {...toggleButtonProps}
              >
                {startAdornment ? (
                  <StartAdornment disabled={disabled} type={startAdornmentType}>
                    {startAdornment}
                  </StartAdornment>
                ) : null}
                <Text
                  disabled={disabled}
                  id={id}
                  primary={primary}
                  secondary={secondary}
                />
                {/* Expand/Collapse icons are built-in when nested */}
                {!disabled ? (
                  <span className="pr-2">
                    <EndAdornment>
                      {state.open ? <ExpandLess /> : <ExpandMore />}
                    </EndAdornment>
                  </span>
                ) : null}
              </button>
              {/* If nested, any endAdornment has to sit outside, as we don't
              know if it's interactive. To avoid no-nested-
              interactive-elements */}
              {endAdornment && !disabled ? (
                <EndAdornment type={endAdornmentType}>
                  {endAdornment}
                </EndAdornment>
              ) : null}
            </span>
            {state.open ? components.nestedList : null}
          </>
        ) : (
          <span
            className={clsx(
              'flex items-center',
              indents[nested],
              !disabled && button && 'hover:bg-black-5 active:bg-black-10',
              selected ? 'bg-primary-10' : '',
            )}
          >
            <ChildComponent
              className={clsx(
                'flex w-full truncate px-4 cursor-default items-center',
                endAdornment ? 'pr-0' : '',
                vertPadding,
                textStyle[state.itemState].primary,
                button ? buttonStyles[state.itemState] : '',
                getLeftPadding(),
              )}
              {...(button ? buttonProps : {})}
            >
              {isCheckbox ? (
                getCheckbox()
              ) : (
                <>
                  <StartAdornment disabled={disabled} type={startAdornmentType}>
                    {startAdornment}
                  </StartAdornment>
                  <Text
                    disabled={disabled}
                    id={id}
                    primary={primary}
                    secondary={secondary}
                  />
                </>
              )}
              {!disabled && !interactiveEndAdornment ? (
                <EndAdornment type={endAdornmentType}>
                  {endAdornment}
                </EndAdornment>
              ) : null}
            </ChildComponent>
            {/* Interactive elements must be HTML siblings, never children */}
            {!disabled && interactiveEndAdornment ? (
              <EndAdornment type={endAdornmentType}>
                {endAdornment}
              </EndAdornment>
            ) : null}
          </span>
        )}
      </li>
    </ItemContext.Provider>
  )
}

function EndAdornment({
  children,
  nested = false,
  type = 'icon',
}: PropsWithChildren<EndAdornmentProps>): JSX.Element | null {
  if (!children) return null

  return (
    <div
      className={clsx(
        'flex items-center h-full flex-shrink-0',
        nested ? '' : 'pr-2',
      )}
    >
      <div
        className={clsx(
          type === 'element' || type === 'icon-button'
            ? 'flex'
            : 'flex w-6 h-6',
        )}
      >
        {children}
      </div>
    </div>
  )
}

function StartAdornment({
  children,
  type = 'icon',
  disabled = false,
}: PropsWithChildren<StartAdornmentProps>): JSX.Element | null {
  if (!children) return null

  return (
    <div
      className={clsx(
        'flex items-center',
        type === 'avatar' ? 'min-w-14' : 'min-w-11',
        type === 'icon' ? 'h-6' : 'h-10',
        type === 'checkbox' ? 'w-full' : '',
        disabled ? 'opacity-50 cursor-not-allowed' : '',
      )}
    >
      <div
        className={clsx(
          'flex items-center',
          type === 'icon' ? 'w-6 h-6' : 'w-10 h-10',
          type === 'checkbox' ? 'justify-start -m-2 w-full' : 'justify-center',
        )}
      >
        {children}
      </div>
    </div>
  )
}

function Text({
  id,
  primary,
  secondary,
  disabled = false,
}: TextProps): JSX.Element | null {
  const { dense } = useList()
  const [{ itemState }] = useItem()

  if (!primary && !secondary) return null

  return (
    <div
      className={clsx(
        'flex-grow truncate',
        disabled ? 'cursor-not-allowed' : '',
      )}
    >
      <div
        className={clsx(
          'flex flex-col flex-grow mr-2 items-start',
          secondary ? 'my-1.5' : 'my-1',
        )}
      >
        <span
          className={clsx(
            'inline-block w-full break-all',
            'font-sans whitespace-normal text-left',
            dense ? 'text-sm leading-5' : 'text-base leading-6',
            textStyle[itemState].primary,
          )}
          id={id}
        >
          {primary}
        </span>
        {secondary ? (
          <span
            className={clsx(
              'inline-block w-full break-all font-sans text-sm leading-5 whitespace-normal text-left',
              textStyle[itemState].secondary,
            )}
          >
            {secondary}
          </span>
        ) : null}
      </div>
    </div>
  )
}

export default Item
